import React, {Component, Fragment} from 'react'
import {observer} from 'mobx-react'

import InlineCode from 'lib/struct/InlineCode'

import Field from '../../struct/Field'
import SettingsGroup from '../../struct/SettingsGroup'
import NestedField from '../../struct/NestedField'
import Hint from '../../struct/Hint'
import TagSourceField from '../../struct/TagSourceField'
import Select from '../../control/DependentSelect'
import Checkbox from '../../control/DependentCheckbox'
import InputText from '../../control/DependentInputText'
import ProfileCatalogEntitiesSelect from './ProfileCatalogEntitiesSelect'
import ProfileCatalogStocksSelect from './ProfileCatalogStocksSelect'

import * as SelectionMode from '../../../constant/ProfileCatalogSelectionMode'
import * as StockSelectionMode from '../../../constant/ProfileCatalogStockSelectionMode'

import styles from './ProfileCatalogSettings.less'

const {
    'min-price-input': min_price_input_class,
    'checkbox-block': checkbox_block_class,
    'filter-checkbox-label': filter_checkbox_label_class,
    'selection-mode__wrapper': selection_mode_wrapper_class,
    'selection-mode__select': selection_mode_select_class,
    'export-ignore-param': export_ignore_param_class,
} = styles

const bool_options =
    [{
        label: 'Пропустить',
        value: '',
    }, {
        label: 'Да',
        value: 'true',
    }, {
        label: 'Нет',
        value: 'false',
    }]

const bool_options_numeric =
    [{
        label: 'Пропустить',
        value: '',
    }, {
        label: 'Да',
        value: '1',
    }, {
        label: 'Нет',
        value: '0',
    }]

@observer export default class ProfileCatalogSettings extends Component {
    render() {
        const {title} = this.props
        /** @type {ProfileSettingsContext} */
        const context = this.props.context
        const profile = context.profile
        const catalog_selection = profile.catalog_selection

        const with_categories = context.categories.length
        const with_sets = context.sets.length
        const with_types = context.product_types.length

        return (
            <SettingsGroup title={title}>
                <Field label="Выбрать товары">
                    {
                        with_categories || with_sets || with_types ? (
                            <NestedField>
                                <Select
                                    className={selection_mode_select_class}
                                    options={SelectionMode.control_options}
                                    value={catalog_selection.selection_mode}
                                    onChange={e => catalog_selection.setSelectionMode(e.target.value)}
                                    state={context.form_state}
                                />

                                {
                                    catalog_selection.selection_mode === SelectionMode.SELECTED_ENTITIES ? (
                                        <ProfileCatalogEntitiesSelect context={context}/>
                                    ) : (null)
                                }
                            </NestedField>
                        ) : (null)
                    }
                </Field>

                <Field label="Выбрать склады">
                    <NestedField>
                        <Select
                            options={StockSelectionMode.control_options}
                            value={profile.catalog_stocks_selection_mode}
                            onChange={e => profile.catalog_stocks_selection_mode = e.target.value}
                            state={context.form_state}
                        />

                        {
                            profile.catalog_stocks_selection_mode === StockSelectionMode.SELECTED_STOCKS ? (
                                <ProfileCatalogStocksSelect context={context}/>
                            ) : (null)
                        }

                        <Hint>Наличие и остаток товаров будет определятся по выбранным складам.</Hint>
                    </NestedField>
                </Field>

                <Field label="Фильтр">
                    <Checkbox
                        className={checkbox_block_class}
                        labelClassName={filter_checkbox_label_class}
                        checked={profile.catalog_selection_skip_products_with_ignore_param}
                        onChange={e => profile.catalog_selection_skip_products_with_ignore_param = e.target.checked}
                        state={context.form_state}
                    >
                        <span className={styles['label_with-code']}>Товары, у которых в дополнительных параметрах указано значение <InlineCode
                            className={export_ignore_param_class} stopEvent>yandexmarket.ignored=1</InlineCode>, не будут экспортированы в YML-файл.</span>
                        <Hint>Добавить дополнительный параметр возможно при редактировании товара, на вкладке «Описание
                            и SEO», в поле «Дополнительные параметры».</Hint>
                    </Checkbox>
                </Field>

                <Field label="Скрытые товары">
                    <Checkbox
                        className={checkbox_block_class}
                        checked={profile.catalog_selection_export_hidden_products}
                        onChange={e => profile.catalog_selection_export_hidden_products = e.target.checked}
                        state={context.form_state}
                    >
                        Выгружать скрытые товары
                        <Hint>Распространяется только на настройку видимости товара: недоступные для заказа артикулы
                            выгружены не будут.</Hint>
                    </Checkbox>
                </Field>

                <Field label="Товары, которых нет в наличии">
                    <Checkbox
                        className={checkbox_block_class}
                        checked={profile.catalog_selection_export_out_of_stock}
                        onChange={e => profile.catalog_selection_export_out_of_stock = e.target.checked}
                        state={context.form_state}
                    >
                        Выгружать товары, которых нет в наличии
                    </Checkbox>
                </Field>

                <Field>
                    <Checkbox
                        className={checkbox_block_class}
                        checked={!profile.catalog_selection_export_infinity_stock}
                        onChange={e => profile.catalog_selection_export_infinity_stock = !e.target.checked}
                        state={context.form_state}
                    >
                        Не выгружать товары с остатком ∞
                    </Checkbox>
                </Field>

                <Field label="Товары без изображений">
                    <Checkbox
                        className={checkbox_block_class}
                        checked={profile.catalog_selection_export_without_images}
                        onChange={e => profile.catalog_selection_export_without_images = e.target.checked}
                        state={context.form_state}
                    >
                        Выгружать товары без изображений
                    </Checkbox>
                </Field>

                <Field label="Минимальная цена">
                    <div>
                        <InputText
                            value={profile.catalog_selection_min_exported_price}
                            onChange={e => profile.catalog_selection_min_exported_price = e.target.value}
                            placeholder="0"
                            className={min_price_input_class}
                            state={context.form_state}
                        />
                        <span>{profile.catalog_selection_min_exported_price_currency}</span>
                    </div>
                    <Hint>Товары с ценой меньше указанной не будут экспортированы в YML-файл</Hint>
                </Field>

                <Field label="Артикулы">
                    <Checkbox
                        checked={profile.catalog_export_separate_skus}
                        onChange={e => profile.catalog_export_separate_skus = e.target.checked}
                        state={context.form_state}
                    >
                        Экспортировать каждый артикул товара как отдельную позицию
                    </Checkbox>

                    <Checkbox
                        className={checkbox_block_class}
                        checked={!profile.catalog_selection_export_skus_without_personal_image}
                        onChange={e => profile.catalog_selection_export_skus_without_personal_image = !e.target.checked}
                        disabled={!profile.catalog_export_separate_skus || profile.catalog_selection_export_without_images}
                        state={context.form_state}
                    >
                        Не выгружать артикул, если у него не задано изображение
                    </Checkbox>
                </Field>

                <TagSourceField label="Скидки" tag="enable_auto_discounts">
                    <Select
                        options={bool_options}
                        value={profile.shop_enable_auto_discounts}
                        onChange={e => profile.shop_enable_auto_discounts = e.target.value}
                        state={context.form_state}
                    />
                    <Hint>Автоматический расчет и показ скидок для всего прайс-листа</Hint>
                </TagSourceField>

                <TagSourceField label="Цена от" tag={<Fragment>offer{'><'}price <b>from="true"</b></Fragment>}>
                    <Checkbox
                        checked={profile.shop_is_from_price}
                        onChange={e => profile.shop_is_from_price = e.target.checked}
                        state={context.form_state}
                    >
                        Атрибут "цена от"
                    </Checkbox>
                </TagSourceField>
                <TagSourceField label="Формат SKU">
                    <div>
                        <InputText
                            value={profile.catalog_offer_id_template}
                            onChange={e => profile.catalog_offer_id_template = e.target.value}
                            placeholder="{$offer.id}"
                            state={context.form_state}
                        />
                    </div>
                    <Hint><b>{"{$offer.id}"}</b> будет заменён на фактический номер. Не удаляйте фрагмент <b>{"{$offer.id}"}</b> из этой строки.</Hint>
                </TagSourceField>
                <TagSourceField label='Возможность заказать товар на маркетплейсе "Яндекс.Покупки"' tag={'cpa'}>
                    <Select
                        options={bool_options_numeric}
                        value={profile.shop_cpa}
                        onChange={e => profile.shop_cpa = e.target.value}
                        state={context.form_state}
                    />
                    <Hint>Глобальный параметр cpa для для элемента {'<shop>'}.<br/>
                        Обработка заказов возможна вручную в партнерском интерфейсе «Яндекс.Маркета» или автоматически
                        через API.<br/>
                        Это значение может быть переопределено для отдельных товаров в секции «Экспорт характеристик
                        товаров».</Hint>
                </TagSourceField>
            </SettingsGroup>
        )
    }
}